/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  ShieldCheck, User, Mail, Lock, ArrowLeft, ArrowRight,
  KeyRound, Eye, EyeOff, BarChart3, Users, FileText,
} from 'lucide-react';
import axios from 'axios';
import './AdminLoginModule.css';

/* ─── WHITELIST ─── */
const validAdmins = [
  { email: 'PawanKumar.Sharma@talentoza.com', username: 'Pawan' },
  { email: 'Shweta.Srivastava@talentoza.com',     username: 'Shweta Srivastava' },
];

/* ════════════════════════════════════════════
   SUCCESS OVERLAY — Clean & Professional
   ════════════════════════════════════════════ */
function SuccessOverlay({ name, onDone }) {
  const [phase, setPhase]       = useState('enter');
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('check'),  150);
    const t2 = setTimeout(() => setPhase('text'),   600);
    const t3 = setTimeout(() => setPhase('bar'),    900);
    const t4 = setTimeout(() => setPhase('exit'),  2600);
    const t5 = setTimeout(() => onDone(),          3000);
    return () => [t1, t2, t3, t4, t5].forEach(clearTimeout);
  }, [onDone]);

  useEffect(() => {
    if (phase !== 'bar') return;
    const start = performance.now();
    const dur   = 1600;
    const tick  = (now) => {
      const pct = Math.min((now - start) / dur * 100, 100);
      setProgress(pct);
      if (pct < 100) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]);

  return ReactDOM.createPortal(
    <div className={`adm-suc-overlay ${phase === 'exit' ? 'adm-suc-exit' : ''}`}>

      {/* Subtle grid background */}
      <div className="adm-suc-grid" />

      {/* Ambient glow blobs */}
      <div className="adm-suc-blob adm-suc-blob--1" />
      <div className="adm-suc-blob adm-suc-blob--2" />

      {/* Center card */}
      <div className={`adm-suc-card ${phase !== 'enter' ? 'adm-suc-card--in' : ''}`}>

        {/* Check circle */}
        <div className="adm-suc-check-wrap">
          {/* Pulse ring */}
          <div className={`adm-suc-pulse ${phase !== 'enter' ? 'adm-suc-pulse--in' : ''}`} />

          <svg
            className={`adm-suc-svg ${phase !== 'enter' ? 'adm-suc-svg--in' : ''}`}
            viewBox="0 0 52 52"
          >
            <defs>
              <linearGradient id="admCircleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%"   stopColor="#10b981" />
                <stop offset="100%" stopColor="#34d399" />
              </linearGradient>
            </defs>
            <circle
              className="adm-suc-circle"
              cx="26" cy="26" r="24"
            />
            <path
              className="adm-suc-path"
              d="M14 26 l8 8 l16-16"
            />
          </svg>
        </div>

        {/* Text */}
        <div className={`adm-suc-texts ${
          phase === 'text' || phase === 'bar' || phase === 'exit'
            ? 'adm-suc-texts--in' : ''
        }`}>
          <div className="adm-suc-tag">
            <span className="adm-suc-tag-dot" />
            ACCESS GRANTED
          </div>
          <h2 className="adm-suc-h">
            Welcome back{name ? `, ${name.split(' ')[0]}` : ''}
          </h2>
          <p className="adm-suc-p">
            Identity verified · Session secured · Redirecting
          </p>
        </div>

        {/* Progress bar */}
        <div className={`adm-suc-bar-track ${
          phase === 'bar' || phase === 'exit' ? 'adm-suc-bar-track--in' : ''
        }`}>
          <div
            className="adm-suc-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

      </div>
    </div>,
    document.body
  );
}

/* ─── Password Input ─── */
function PwInput({ name, placeholder, onChange, icon: Icon = Lock }) {
  const [show, setShow] = useState(false);
  return (
    <div className="adm-iw">
      <Icon size={18} className="adm-iw-icon" />
      <input
        className="adm-input"
        type={show ? 'text' : 'password'}
        name={name}
        placeholder={placeholder}
        onChange={onChange}
        required
        autoComplete="off"
      />
      <button
        type="button"
        className="adm-pw-toggle"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      <span className="adm-fline" />
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function AdminLoginModule({ onLoginSuccess, onBack }) {
  const [view, setView]               = useState('login');
  const [animKey, setAnimKey]         = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loggedInName, setLoggedInName] = useState('');
  const leftRef = useRef(null);
  const partRef = useRef(null);

  const switchView = useCallback((v) => {
    setAnimKey((k) => k + 1);
    setView(v);
  }, []);

  /* ── Particles ── */
  useEffect(() => {
    const c = partRef.current;
    if (!c) return;
    const ps = [];
    for (let i = 0; i < 30; i++) {
      const p  = document.createElement('div');
      p.className = 'adm-pt';
      const sz = Math.random() * 2 + 0.8;
      p.style.cssText = `
        left:${Math.random() * 100}%;
        bottom:${Math.random() * -15}%;
        width:${sz}px; height:${sz}px;
        animation-duration:${Math.random() * 18 + 14}s;
        animation-delay:${Math.random() * 12}s;
      `;
      c.appendChild(p);
      ps.push(p);
    }
    return () => ps.forEach((p) => p.remove());
  }, []);

  /* ── Orb parallax ── */
  const onMouse = useCallback((e) => {
    const l = leftRef.current;
    if (!l) return;
    const r  = l.getBoundingClientRect();
    const cx = r.left + r.width  / 2;
    const cy = r.top  + r.height / 2;
    const dx = (e.clientX - cx) / (r.width  / 2);
    const dy = (e.clientY - cy) / (r.height / 2);
    l.querySelectorAll('.adm-orb').forEach((o, i) => {
      const sp = (i + 1) * 12;
      o.style.transform = `translate(${dx * sp}px,${dy * sp}px)`;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, [onMouse]);

  const handleLoginSuccess = useCallback((name) => {
    setLoggedInName(name || '');
    setShowSuccess(true);
  }, []);

  const handleOverlayDone = useCallback(() => {
    setShowSuccess(false);
    if (onLoginSuccess) onLoginSuccess();
  }, [onLoginSuccess]);

  const subtitles = {
    login:  'Restricted access. Authorized personnel only.',
    forgot: 'Reset your administrator password securely.',
  };

  return (
    <div className="adm-w">

      {/* Portal overlay — outside adm-w entirely */}
      {showSuccess && (
        <SuccessOverlay name={loggedInName} onDone={handleOverlayDone} />
      )}

      {/* ━━━ LEFT PANEL ━━━ */}
      <div className="adm-left" ref={leftRef}>
        <div className="adm-left-grid" />
        <div className="adm-orb adm-orb--1" />
        <div className="adm-orb adm-orb--2" />
        <div className="adm-orb adm-orb--3" />
        <div className="adm-parts" ref={partRef} />
        <div className="adm-shapes">
          <div className="adm-sh adm-sh--1" />
          <div className="adm-sh adm-sh--2" />
          <div className="adm-sh adm-sh--3" />
          <div className="adm-sh adm-sh--4" />
        </div>

        <div className="adm-left-content">
          <div className="adm-left-brand">
            <div className="adm-brand-box">
              <ShieldCheck size={24} color="#fff" />
            </div>
            <span className="adm-brand-name">TalentOza</span>
          </div>
          <h1 className="adm-left-h">
            Admin<br />
            <span className="adm-gr">Control Center</span>
          </h1>
          <p className="adm-left-p">
            Manage vendors, monitor compliance, and oversee your
            entire workforce from one powerful dashboard.
          </p>
          <div className="adm-left-feats">
            <div className="adm-left-feat">
              <div className="adm-feat-icon">
                <Users size={18} color="#60a5fa" />
              </div>
              <div className="adm-feat-text">
                <h4>User Management</h4>
                <p>Full control over roles, permissions, and access levels</p>
              </div>
            </div>
            <div className="adm-left-feat">
              <div className="adm-feat-icon">
                <BarChart3 size={18} color="#a78bfa" />
              </div>
              <div className="adm-feat-text">
                <h4>Real-Time Analytics</h4>
                <p>Live dashboards with actionable insights</p>
              </div>
            </div>
            <div className="adm-left-feat">
              <div className="adm-feat-icon">
                <FileText size={18} color="#34d399" />
              </div>
              <div className="adm-feat-text">
                <h4>Compliance Monitoring</h4>
                <p>Automated audits and regulatory tracking</p>
              </div>
            </div>
          </div>
        </div>

        <div className="adm-left-bottom">
          <div className="adm-trust">
            <span className="adm-trust-dot" />
            <span className="adm-trust-text">
              <strong>256-bit encrypted</strong> · SOC2 compliant · 99.9% uptime
            </span>
          </div>
        </div>
      </div>

      {/* ━━━ RIGHT PANEL ━━━ */}
      <div className="adm-right">
        {onBack && (
          <button className="adm-back" onClick={onBack}>
            <ArrowLeft size={16} /> Back
          </button>
        )}
        <div className="adm-form-card">
          <div className="adm-header">
            <div className="adm-icon-wrap">
              <div className="adm-icon-box">
                <ShieldCheck size={30} color="var(--a-pri)" />
              </div>
              <div className="adm-lock-badge">
                <Lock size={11} color="#fff" />
              </div>
            </div>
            <h1 className="adm-h1">Welcome Back</h1>
            <p className="adm-sub">{subtitles[view]}</p>
            <div className="adm-sec-badge">
              <span className="adm-sec-dot" />
              Secure Connection
            </div>
          </div>

          <div className="adm-view" key={animKey}>
            {view === 'login' && (
              <LoginForm
                onSwitchView={switchView}
                onLoginSuccess={handleLoginSuccess}
              />
            )}
            {view === 'forgot' && (
              <ForgotPasswordForm onSwitchView={switchView} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LOGIN FORM
   ═══════════════════════════════════════════ */
function LoginForm({ onSwitchView, onLoginSuccess }) {
  const [formData, setFormData] = useState({ loginIdentifier: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    const input = formData.loginIdentifier.trim();
    const matchedAdmin = validAdmins.find(
      (a) =>
        a.email.toLowerCase()    === input.toLowerCase() ||
        a.username.toLowerCase() === input.toLowerCase()
    );
    if (!matchedAdmin) {
      alert('Access Denied: You are not an authorized Administrator.');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/admin/login`,
        formData
      );
      onLoginSuccess(matchedAdmin.username);
    } catch (err) {
      console.error('Login Error:', err);
      alert(err.response?.data?.message || 'Login Failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={handleLogin}>
      <div className="adm-fg">
        <label className="adm-label">Email or Username</label>
        <div className="adm-iw">
          <User size={18} className="adm-iw-icon" />
          <input
            className="adm-input"
            type="text"
            name="loginIdentifier"
            onChange={handleChange}
            placeholder="Enter authorized ID"
            required
            autoComplete="off"
          />
          <span className="adm-fline" />
        </div>
      </div>

      <div className="adm-fg">
        <label className="adm-label">Password</label>
        <PwInput name="password" placeholder="Enter password" onChange={handleChange} />
      </div>

      <div className="adm-actions">
        <button
          type="button"
          className="adm-link"
          onClick={() => onSwitchView('forgot')}
        >
          Forgot Password?
        </button>
      </div>

      <button
        type="submit"
        className={`adm-btn ${isLoading ? 'adm-btn--loading' : ''}`}
        disabled={isLoading}
      >
        <span>{isLoading ? 'Authenticating…' : 'Secure Login'}</span>
        {!isLoading && <ArrowRight size={18} />}
      </button>

      <div className="adm-divider">
        <span className="adm-div-line" />
        <span className="adm-div-text">Monitored System</span>
        <span className="adm-div-line" />
      </div>

      <div className="adm-monitor">
        <span className="adm-mon-dot" />
        Access is logged and actively monitored
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════
   FORGOT PASSWORD FORM
   ═══════════════════════════════════════════ */
function ForgotPasswordForm({ onSwitchView }) {
  const [formData, setFormData] = useState({
    email: '', newPassword: '', confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleReset = async (e) => {
    e.preventDefault();
    const isAuthorized = validAdmins.some(
      (a) => a.email.toLowerCase() === formData.email.trim().toLowerCase()
    );
    if (!isAuthorized) {
      alert('Access Denied: This email is not authorized.');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/admin/forgot-password`,
        {
          email:           formData.email,
          newPassword:     formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }
      );
      alert('Password updated successfully. Please login.');
      onSwitchView('login');
    } catch (err) {
      console.error('Reset Error:', err);
      alert(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={handleReset}>
      <div className="adm-fg">
        <label className="adm-label">Authorized Email</label>
        <div className="adm-iw">
          <Mail size={18} className="adm-iw-icon" />
          <input
            className="adm-input"
            type="email"
            name="email"
            onChange={handleChange}
            placeholder="Enter registered email"
            required
            autoComplete="off"
          />
          <span className="adm-fline" />
        </div>
      </div>

      <div className="adm-fg">
        <label className="adm-label">New Password</label>
        <PwInput
          name="newPassword"
          placeholder="Enter new password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <div className="adm-fg">
        <label className="adm-label">Confirm New Password</label>
        <PwInput
          name="confirmPassword"
          placeholder="Confirm new password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <button
        type="submit"
        className={`adm-btn ${isLoading ? 'adm-btn--loading' : ''}`}
        disabled={isLoading}
      >
        <span>{isLoading ? 'Updating…' : 'Reset Password'}</span>
        {!isLoading && <ArrowRight size={18} />}
      </button>

      <button
        type="button"
        className="adm-back-btn"
        onClick={() => onSwitchView('login')}
      >
        <ArrowLeft size={16} /> Back to Login
      </button>
    </form>
  );
}
